/**
 * Estratégia de links internos automáticos para páginas de imóvel.
 */

import { slugify } from './urlHelpers';

const PRICE_RANGE_PERCENT = 0.25;
const AREA_RANGE_PERCENT = 0.2;

function isPublicProperty(prop) {
  return prop && prop.isAvailable !== false && prop.isInactive !== true;
}

function getPrice(prop) {
  const price = prop.purpose === 'rent' ? prop.rent : prop.price;
  return parseFloat(price) || 0;
}

function parseCount(value) {
  const parsed = parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseArea(value) {
  const parsed = parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function scoreCountMatch(targetValue, candidateValue, exactPoints, closePoints = 0) {
  const target = parseCount(targetValue);
  const candidate = parseCount(candidateValue);
  if (target === null || candidate === null || target <= 0) return 0;
  if (target === candidate) return exactPoints;
  if (closePoints > 0 && Math.abs(target - candidate) === 1) return closePoints;
  return 0;
}

function scoreAreaMatch(targetArea, candidateArea) {
  if (!targetArea || !candidateArea) return 0;
  const diff = Math.abs(targetArea - candidateArea) / targetArea;
  if (diff <= AREA_RANGE_PERCENT) return 12;
  if (diff <= AREA_RANGE_PERCENT * 2) return 6;
  return 0;
}

function scoreSimilarity(target, candidate) {
  if (!candidate || candidate.id === target.id) return -1;
  if (!isPublicProperty(candidate)) return -1;

  let score = 0;
  if (candidate.type === target.type) score += 30;
  if (candidate.neighborhood === target.neighborhood && target.neighborhood) score += 25;
  if (candidate.city === target.city && target.city) score += 15;
  if (candidate.purpose === target.purpose) score += 10;

  if (
    target.condominiumId &&
    candidate.condominiumId === target.condominiumId
  ) {
    score += 15;
  }

  const targetPrice = getPrice(target);
  const candidatePrice = getPrice(candidate);
  if (targetPrice > 0 && candidatePrice > 0) {
    const diff = Math.abs(targetPrice - candidatePrice) / targetPrice;
    if (diff <= PRICE_RANGE_PERCENT) score += 20;
    else if (diff <= PRICE_RANGE_PERCENT * 2) score += 10;
  }

  score += scoreCountMatch(target.bedrooms, candidate.bedrooms, 8, 4);
  score += scoreCountMatch(target.suites, candidate.suites, 8, 4);
  score += scoreCountMatch(target.bathrooms, candidate.bathrooms, 5, 2);
  score += scoreCountMatch(target.parkingSpaces, candidate.parkingSpaces, 5, 2);

  score += scoreAreaMatch(parseArea(target.area), parseArea(candidate.area));

  return score;
}

export function findSimilarProperties(target, allProperties, limit = 6) {
  return (allProperties || [])
    .map((prop) => ({ prop, score: scoreSimilarity(target, prop) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ prop }) => prop);
}

export function findSameNeighborhoodProperties(target, allProperties, limit = 6) {
  if (!target?.neighborhood) return [];
  return (allProperties || [])
    .filter(
      (p) =>
        isPublicProperty(p) &&
        p.id !== target.id &&
        p.neighborhood === target.neighborhood &&
        p.city === target.city
    )
    .slice(0, limit);
}

export function findSameCondominiumProperties(target, allProperties, limit = 6) {
  if (!target?.condominiumId) return [];
  return (allProperties || [])
    .filter(
      (p) =>
        isPublicProperty(p) &&
        p.id !== target.id &&
        p.condominiumId === target.condominiumId
    )
    .slice(0, limit);
}

function buildCondominiumListingPath(target) {
  if (!target?.condominiumId) return null;
  const params = new URLSearchParams({ condominiumId: target.condominiumId });
  if (target.condominiumTitle) {
    params.set('condominiumName', target.condominiumTitle);
  }
  return `/propriedades?${params.toString()}`;
}

function pushUniqueSection(sections, usedIds, section, candidates, limit = 4) {
  const links = candidates
    .filter((prop) => !usedIds.has(prop.id))
    .slice(0, limit);
  if (!links.length) return;
  links.forEach((prop) => usedIds.add(prop.id));
  sections.push({ ...section, links });
}

export function buildInternalLinkSections(target, allProperties) {
  const sections = [];
  const usedIds = new Set([target.id]);

  if (target.condominiumId) {
    pushUniqueSection(
      sections,
      usedIds,
      {
        id: 'condominium',
        title: target.condominiumTitle
          ? `Imóveis no ${target.condominiumTitle}`
          : 'Imóveis no mesmo condomínio',
        listingPath: buildCondominiumListingPath(target),
      },
      findSameCondominiumProperties(target, allProperties, 8)
    );
  }

  if (target.neighborhood) {
    pushUniqueSection(
      sections,
      usedIds,
      {
        id: 'neighborhood',
        title: `Mais imóveis no bairro ${target.neighborhood}`,
        listingPath: target.city
          ? `/propriedades/${slugify(target.city)}/${slugify(target.neighborhood)}`
          : null,
      },
      findSameNeighborhoodProperties(target, allProperties, 8)
    );
  }

  pushUniqueSection(
    sections,
    usedIds,
    {
      id: 'similar',
      title: 'Imóveis semelhantes',
    },
    findSimilarProperties(target, allProperties, 8)
  );

  return sections;
}
